// src/features/auth/login-page.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input, PasswordInput } from '@/components/ui/input';
import { useAuthStore } from '@/auth/auth-store';
import { useLoginFlow } from '@/auth/hooks';
import { credentialsSchema, type CredentialsValues } from './login-schema';
import { MfaDialog } from './mfa-dialog';

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const status = useAuthStore((s) => s.status);
  const {
    submitCredentials,
    submitCode,
    cancelMfa,
    mfaOpen,
    mfaMode,
    enrollData,
    error,
    isPending,
  } = useLoginFlow();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    if (status === 'authenticated') {
      const target = search.redirect?.startsWith('/') ? search.redirect : '/';
      void navigate({ to: target });
    }
  }, [status, navigate, search.redirect]);

  const onSubmit = handleSubmit((v) => submitCredentials(v.email, v.password));

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-brand-red p-12 md:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-bg font-bold">
            KO
          </div>
          <span className="text-sm font-semibold text-text-primary">
            KO Collectibles · Consola de administración
          </span>
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-text-primary">
            Operaciones internas
          </h1>
          <p className="mt-3 max-w-sm text-sm text-text-primary/80">
            Gestiona publicaciones, disputas, torneos, pagos y la salud del ecosistema de
            coleccionistas KO.
          </p>
        </div>
        <span className="text-xs text-text-primary/60">KO · Confidencial</span>
      </div>

      <div className="flex items-center justify-center bg-bg p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5" noValidate>
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">Iniciar sesión</h2>
            <p className="text-sm text-text-muted">Solo personal autorizado</p>
          </div>

          <div className="space-y-1">
            <label htmlFor="email" className="text-xs uppercase tracking-wide text-text-muted">
              Correo electrónico
            </label>
            <Input id="email" type="email" autoComplete="username" {...register('email')} />
            {errors.email && <p className="text-xs text-red">{errors.email.message}</p>}
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="text-xs uppercase tracking-wide text-text-muted">
              Contraseña
            </label>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && <p className="text-xs text-red">{errors.password.message}</p>}
          </div>

          {error && !mfaOpen && (
            <p role="alert" className="text-sm text-red">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? 'Autenticando…' : 'Continuar'}
          </Button>
        </form>
      </div>

      <MfaDialog
        open={mfaOpen}
        mode={mfaMode}
        enrollData={enrollData}
        pending={isPending}
        error={mfaOpen ? error : null}
        onSubmit={submitCode}
        onOpenChange={(o) => {
          if (!o) cancelMfa();
        }}
      />
    </div>
  );
}
